import { api } from './client';

export interface BomComponentDto {
  id?: string;
  componentProductId: string;
  qty: number;
  unit?: string;
  wastagePercent?: number;
  component?: { id: string; name: string; unit: string; rate: number; stock?: number };
}

export interface BomDto {
  id: string;
  tenantId: string;
  productId: string;
  product?: { id: string; name: string; unit: string; rate: number };
  version: string;
  isActive: boolean;
  notes?: string;
  directLabourPerUnit?: number;
  directOverheadPerUnit?: number;
  createdAt: string;
  components: BomComponentDto[];
}

export interface ManufacturingOrderDto {
  id: string;
  tenantId: string;
  orderNumber: string;
  bomId: string;
  bom?: BomDto;
  plannedQty: number;
  completedQty: number;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  startDate?: string;
  endDate?: string;
  notes?: string;
  createdAt: string;
  batches?: ProductionBatchDto[];
}

export interface ProductionBatchDto {
  id: string;
  orderId: string;
  batchNumber: string;
  startedAt: string;
  completedAt?: string;
  actualQty?: number;
  actualCost?: number;
  notes?: string;
}

export interface CostVarianceDto {
  orderId: string;
  orderNumber: string;
  product: string;
  completedQty: number;
  plannedCostPerUnit: number;
  actualCostPerUnit: number;
  varianceAmount: number;
  variancePct: number;
}

export const manufacturingApi = {
  // BOMs
  listBoms: () => api.get<BomDto[]>('/manufacturing/boms'),
  getBom: (id: string) => api.get<BomDto>(`/manufacturing/boms/${id}`),
  calcBomCost: (id: string, qty = 1) => api.get<unknown>(`/manufacturing/boms/${id}/cost?qty=${qty}`),
  createBom: (dto: {
    productId: string; version?: string; notes?: string;
    directLabourPerUnit?: number; directOverheadPerUnit?: number;
    components: { componentProductId: string; qty: number; unit?: string; wastagePercent?: number }[];
  }) => api.post<BomDto>('/manufacturing/boms', dto),
  updateBom: (id: string, dto: {
    version?: string; notes?: string; isActive?: boolean;
    directLabourPerUnit?: number; directOverheadPerUnit?: number;
    components?: { componentProductId: string; qty: number; unit?: string; wastagePercent?: number }[];
  }) => api.patch<BomDto>(`/manufacturing/boms/${id}`, dto),

  // Manufacturing Orders
  listOrders: (status?: string) =>
    api.get<ManufacturingOrderDto[]>(`/manufacturing/orders${status ? `?status=${status}` : ''}`),
  getOrder: (id: string) => api.get<ManufacturingOrderDto>(`/manufacturing/orders/${id}`),
  getCostVariance: () => api.get<CostVarianceDto[]>('/manufacturing/orders/cost-variance'),
  createOrder: (dto: { bomId: string; plannedQty: number; startDate?: string; endDate?: string; notes?: string }) =>
    api.post<ManufacturingOrderDto>('/manufacturing/orders', dto),
  startOrder: (id: string) => api.post<ManufacturingOrderDto>(`/manufacturing/orders/${id}/start`, {}),
  completeOrder: (id: string, dto: { actualQty: number; actualCost?: number; notes?: string }) =>
    api.post<ManufacturingOrderDto>(`/manufacturing/orders/${id}/complete`, dto),
  editCompletion: (id: string, dto: { actualQty: number; actualCost?: number; notes?: string }) =>
    api.patch<ManufacturingOrderDto>(`/manufacturing/orders/${id}/complete`, dto),
  cancelOrder: (id: string) => api.post<ManufacturingOrderDto>(`/manufacturing/orders/${id}/cancel`, {}),
  deleteOrder: (id: string) => api.delete<{ success: boolean }>(`/manufacturing/orders/${id}`),
};
